import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Mail, Check, Send, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import SectionLayout from "@/pages/SectionLayout";
import { devWarn, devError } from "@/utils/devLog";

const EMAIL = "hola@ipandurata.com";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CHIPA_GREETING =
  "Hello there, I am Chipá, Pandurata's concierge; as you may have noticed, I am an agent programed to help you figure out if we will be able to add value to your organization. For starters, may I know who I am speaking with?";

// Matches plain http(s) URLs in agent replies so we can render them as
// real anchor tags (e.g. the calendar link in the State-5 closing message).
const URL_RX = /(https?:\/\/[^\s)]+)/g;

const renderWithLinks = (text) => {
  if (!text) return null;
  const parts = text.split(URL_RX);
  // Use the part's content + position as a stable composite key — the array
  // is recomputed on every render so a bare index would be unstable.
  return parts.map((part, i) =>
    URL_RX.test(part) ? (
      <a
        key={`l-${i}-${part}`}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="chipa-bubble__link"
      >
        {part}
      </a>
    ) : (
      <React.Fragment key={`t-${i}-${part.length}`}>{part}</React.Fragment>
    ),
  );
};

// =========================================================================
// ChipaChat — the conversation panel. Owns its own message list + input.
// =========================================================================
const ChipaChat = ({ onClose }) => {
  const [messages, setMessages] = useState([
    { id: "agent-greeting", role: "agent", text: CHIPA_GREETING },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Autoscroll to bottom when messages change
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    const userMsg = {
      id: `u-${Date.now()}`,
      role: "user",
      text,
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);
    // Show a soft "typing" placeholder while waiting
    const typingId = `t-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: typingId, role: "agent", text: "…", typing: true },
    ]);

    // Artificial latency to maintain the human concierge persona — Chipá
    // is "reading" / "typing" for a believable amount of time.
    const start = Date.now();
    const minDelay = 1500 + Math.random() * 1000; // 1500-2500ms

    try {
      const res = await http.post("/chipa", {
        message: text,
        session_id: sessionRef.current,
      });
      sessionRef.current = res.data.session_id;
      const elapsed = Date.now() - start;
      if (elapsed < minDelay) {
        await new Promise((r) => setTimeout(r, minDelay - elapsed));
      }
      setMessages((prev) =>
        prev
          .filter((m) => m.id !== typingId)
          .concat([
            { id: `a-${Date.now()}`, role: "agent", text: res.data.reply },
          ]),
      );
    } catch (err) {
      const elapsed = Date.now() - start;
      if (elapsed < minDelay) {
        await new Promise((r) => setTimeout(r, minDelay - elapsed));
      }
      setMessages((prev) =>
        prev
          .filter((m) => m.id !== typingId)
          .concat([
            {
              id: `a-err-${Date.now()}`,
              role: "agent",
              text:
                "I'm having trouble reaching the studio at the moment. Please try again in a few seconds.",
            },
          ]),
      );
      devWarn("[Chipá] send failed:", err);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    // Enter to send, Shift+Enter for newline
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(e);
    }
  };

  return (
    <div className="chipa" data-testid="chipa-chat">
      <header className="chipa__head">
        <div className="chipa__title-wrap">
          <Sparkles size={14} strokeWidth={1.6} />
          <h2 className="chipa__title" data-testid="chipa-title">
            Chipá
          </h2>
        </div>
        <button
          type="button"
          className="chipa__close"
          onClick={onClose}
          aria-label="Close chat"
          data-testid="chipa-close"
        >
          <X size={14} strokeWidth={1.6} />
          <span>Close</span>
        </button>
      </header>

      <div className="chipa__stream" ref={scrollRef} data-testid="chipa-stream">
        {messages.map((m) => (
          <div
            key={m.id}
            className={`chipa-bubble chipa-bubble--${m.role} ${
              m.typing ? "chipa-bubble--typing" : ""
            }`}
            data-testid={`chipa-msg-${m.role}`}
          >
            {m.typing ? (
              <span className="chipa-bubble__dots" aria-label="Chipá is typing">
                <span /> <span /> <span />
              </span>
            ) : (
              renderWithLinks(m.text)
            )}
          </div>
        ))}
      </div>

      <form className="chipa__form" onSubmit={send}>
        <textarea
          ref={inputRef}
          className="chipa__input"
          placeholder="Type your reply…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          maxLength={4000}
          data-testid="chipa-input"
        />
        <button
          type="submit"
          className="chipa__send"
          disabled={!input.trim() || sending}
          aria-label="Send"
          data-testid="chipa-send"
        >
          <Send size={16} strokeWidth={1.7} />
        </button>
      </form>
    </div>
  );
};

// =========================================================================
// UnsubscribeModal — minimal modal that posts the visitor's email to
// /api/unsubscribe and shows a farewell message on success.
// =========================================================================
const UnsubscribeModal = ({ onClose }) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const valid = EMAIL_RX.test(email.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await http.post("/unsubscribe", { email: email.trim().toLowerCase() });
      setDone(true);
    } catch (err) {
      setError("Something went wrong, please try again.");
      devWarn("[Pandurata] unsubscribe failed:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="email-modal__backdrop"
      role="dialog"
      aria-modal="true"
      data-testid="unsubscribe-modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form className="email-modal" onSubmit={submit}>
        <button
          type="button"
          className="email-modal__close"
          onClick={onClose}
          aria-label="Close"
          data-testid="unsubscribe-modal-close"
        >
          <X size={16} strokeWidth={1.6} />
        </button>

        {done ? (
          <>
            <p className="email-modal__eyebrow">Removed</p>
            <h3 className="email-modal__title">
              Whenever you are ready, we would love to have you back.
            </h3>
            <div className="email-modal__row email-modal__row--single">
              <button
                type="button"
                className="email-modal__btn"
                onClick={onClose}
                data-testid="unsubscribe-modal-bye"
              >
                <span>Close</span>
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="email-modal__eyebrow">Unsubscribe</p>
            <h3 className="email-modal__title">Take me off the list.</h3>
            <p className="email-modal__body">
              Thank you! Whenever you want, we will adore to have you back.
            </p>
            <input
              type="email"
              className="email-modal__input"
              placeholder="you@somewhere.com"
              autoFocus
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError("");
              }}
              data-testid="unsubscribe-modal-input"
            />
            {error && (
              <p
                className="join-list__status join-list__status--err"
                style={{ margin: 0 }}
              >
                {error}
              </p>
            )}
            <div className="email-modal__row">
              <button
                type="button"
                className="email-modal__btn email-modal__btn--ghost"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="email-modal__btn"
                disabled={!valid || submitting}
                data-testid="unsubscribe-modal-submit"
              >
                <Send size={14} />
                <span>{submitting ? "Sending…" : "Send"}</span>
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

// =========================================================================
// JoinTheList — newsletter subscribe (Substack pipeline). Posts to
// /api/subscribe which dispatches a Resend email to hola@ipandurata.com.
// =========================================================================
const JoinTheList = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [unsubscribeOpen, setUnsubscribeOpen] = useState(false);

  const valid = EMAIL_RX.test(email.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await http.post("/subscribe", { email: email.trim().toLowerCase() });
      setDone(true);
      setEmail("");
      setTimeout(() => setDone(false), 5000);
    } catch (err) {
      setError("Something went wrong, please try again.");
      devWarn("[Pandurata] subscribe failed:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="join-list" data-testid="join-list">
      <h3 className="join-list__title" data-testid="join-list-title">
        Join the List
      </h3>
      <p className="join-list__body">
        Quiet dispatches from the studio. No noise, no marketing, just our
        latest issue featuring technology, culture and science reads.
      </p>

      <form className="join-list__form" onSubmit={submit}>
        <input
          type="email"
          className="join-list__input"
          placeholder="you@somewhere.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError("");
          }}
          disabled={submitting || done}
          required
          aria-label="Email address"
          data-testid="join-list-input"
        />
        <button
          type="submit"
          className="join-list__submit"
          disabled={!valid || submitting || done}
          aria-label={done ? "Subscribed" : "Join the list"}
          data-testid="join-list-submit"
        >
          {done ? (
            <Check size={16} strokeWidth={1.8} />
          ) : (
            <ArrowRight size={16} strokeWidth={1.8} />
          )}
        </button>
      </form>

      <p
        className={`join-list__status ${done ? "join-list__status--ok" : ""} ${
          error ? "join-list__status--err" : ""
        }`}
        role="status"
        aria-live="polite"
        data-testid="join-list-status"
      >
        {done ? (
          "You're on the list."
        ) : error ? (
          error
        ) : (
          <>
            Your email stays private.{" "}
            <button
              type="button"
              className="join-list__unsub"
              onClick={() => setUnsubscribeOpen(true)}
              data-testid="join-list-unsubscribe"
            >
              Unsubscribe
            </button>{" "}
            anytime.
          </>
        )}
      </p>

      {unsubscribeOpen && (
        <UnsubscribeModal onClose={() => setUnsubscribeOpen(false)} />
      )}
    </section>
  );
};

// =========================================================================
// LetsTalk — choices view OR Chipá chat view, plus the email-copy CTA.
// =========================================================================
const LetsTalk = () => {
  const [copied, setCopied] = useState(false);
  const [chipaOpen, setChipaOpen] = useState(false);

  const copyEmail = async () => {
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(EMAIL);
        ok = true;
      }
    } catch (err) {
      devWarn("[Pandurata] clipboard.writeText blocked:", err);
    }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = EMAIL;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "0";
        ta.style.left = "0";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ta.setSelectionRange(0, EMAIL.length);
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (err) {
        devError("[Pandurata] execCommand copy failed:", err);
      }
    }
    if (ok) {
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 1800);
    } else {
      toast.error(`Could not copy automatically — email: ${EMAIL}`, {
        duration: 6000,
      });
    }
  };

  if (chipaOpen) {
    return (
      <SectionLayout>
        <ChipaChat onClose={() => setChipaOpen(false)} />
      </SectionLayout>
    );
  }

  return (
    <SectionLayout>
      <h2 className="glass-panel__title" data-testid="section-title">
        LET&apos;S TALK
      </h2>
      <p className="glass-panel__body" data-testid="section-body">
        Two doors. Pick the one that suits the conversation you want to have.
      </p>

      <div className="lets-talk__choices" data-testid="lets-talk-choices">
        <button
          type="button"
          onClick={() => setChipaOpen(true)}
          className="lets-talk__btn"
          data-testid="btn-chipa"
        >
          <Sparkles size={18} strokeWidth={1.5} />
          <span className="lets-talk__btn-label">Talk to Chipá</span>
          <span className="lets-talk__btn-hint">Book a diagnostic</span>
        </button>

        <button
          type="button"
          onClick={copyEmail}
          className="lets-talk__btn"
          data-testid="btn-email"
          aria-live="polite"
        >
          {copied ? (
            <Check size={18} strokeWidth={1.8} />
          ) : (
            <Mail size={18} strokeWidth={1.5} />
          )}
          <span className="lets-talk__btn-label">{EMAIL}</span>
          <span className="lets-talk__btn-hint">
            {copied ? "Copied to clipboard" : "Click to copy"}
          </span>
        </button>
      </div>

      <JoinTheList />

      <div className="glass-panel__nav">
        <Link to="/" className="glass-panel__btn" data-testid="btn-home">
          Home
        </Link>
      </div>
    </SectionLayout>
  );
};

export default LetsTalk;
